/**
 * Endpoints, health probes, and the forced-local-render switch.
 *
 * The RESOLUTION SOURCE is displayed, not just the URLs. A number on screen is only meaningful
 * if you know which backend produced it, so the page states where it got the endpoint from
 * (query parameter / localStorage / deploy outputs / contract default) and what each health
 * probe actually returned.
 */

import { useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Button from '@cloudscape-design/components/button';
import Box from '@cloudscape-design/components/box';
import Toggle from '@cloudscape-design/components/toggle';
import Alert from '@cloudscape-design/components/alert';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import { IMPB_SHA256, BUILT_AT } from '../lib/impb.js';

function ProbeStatus({ probe }) {
  if (!probe) return <StatusIndicator type="pending">not probed</StatusIndicator>;
  if (probe.placeholder) {
    return (
      <StatusIndicator type="warning">
        reachable, but PLACEHOLDER — HTTP {probe.status} in {probe.ms.toFixed(1)} ms
      </StatusIndicator>
    );
  }
  if (probe.ok) {
    return (
      <StatusIndicator type="success">
        reachable — HTTP {probe.status} in {probe.ms.toFixed(1)} ms
      </StatusIndicator>
    );
  }
  return <StatusIndicator type="error">{probe.error || `HTTP ${probe.status}`}</StatusIndicator>;
}

export default function ConfigPanel({
  endpoints,
  probes,
  live,
  forceLocal,
  onForceLocal,
  onSave,
  onReset,
  probing,
}) {
  const [serialApi, setSerialApi] = useState(endpoints.serialApi);
  const [rulesApi, setRulesApi] = useState(endpoints.rulesApi);

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            description="Resolution order, most specific first: ?serialApi= / ?rulesApi= query parameters → values saved here (localStorage) → outputs/*.json written by the deploy → the docs/api-contract.md defaults."
          >
            Endpoints
          </Header>
        }
      >
        <SpaceBetween size="m">
          <ColumnLayout columns={2}>
            <FormField label="UC1 serial API base URL">
              <Input value={serialApi} onChange={({ detail }) => setSerialApi(detail.value)} data-testid="cfg-serial" />
            </FormField>
            <FormField label="UC2 rules API base URL">
              <Input value={rulesApi} onChange={({ detail }) => setRulesApi(detail.value)} data-testid="cfg-rules" />
            </FormField>
          </ColumnLayout>
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="primary" loading={probing} onClick={() => onSave(serialApi, rulesApi)} data-testid="cfg-save">
              Save &amp; probe health
            </Button>
            <Button
              onClick={async () => {
                const next = await onReset();
                setSerialApi(next.serialApi);
                setRulesApi(next.rulesApi);
              }}
              data-testid="cfg-reset"
            >
              Reset to discovered defaults
            </Button>
          </SpaceBetween>

          <KeyValuePairs
            columns={2}
            items={[
              { label: 'Resolution source', value: <span data-testid="cfg-source">{endpoints.source}</span> },
              {
                label: 'Effective mode',
                value: forceLocal ? (
                  <StatusIndicator type="stopped">Local render (forced)</StatusIndicator>
                ) : live ? (
                  <StatusIndicator type="success">Live</StatusIndicator>
                ) : (
                  <StatusIndicator type="stopped">Local render (no usable backend)</StatusIndicator>
                ),
              },
              { label: 'UC1 /v1/health', value: <ProbeStatus probe={probes.serial} /> },
              { label: 'UC2 /v2/health', value: <ProbeStatus probe={probes.rules} /> },
            ]}
          />

          {(probes.serial && probes.serial.placeholder) || (probes.rules && probes.rules.placeholder) ? (
            <Alert type="warning" header="A reachable endpoint is not the same as a working service">
              At least one endpoint answered with <code>placeholder: true</code> — the infrastructure
              layer's stand-in Lambda, not <code>services/</code>. It returns HTTP 200 and empty
              results, so treating "reachable" as "live" would let the UI present an empty vend as a
              success. It is therefore treated as NOT live.
            </Alert>
          ) : null}
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Local render mode</Header>}>
        <SpaceBetween size="m">
          <Box variant="p">
            With no usable backend, the panels fall back to building barcodes <strong>in the
            browser</strong> from the same <code>impb.js</code> the services use, so barcode rendering
            is reviewable before the services are deployed.
          </Box>
          <Alert type="warning" header="Local render mode produces no measurement and no guarantee">
            There is no high-water mark and no shared buffer, so serials come from a page-local
            counter — <strong>no uniqueness guarantee, no latency measurement</strong>. Every figure
            is badged <code>LOCAL RENDER</code>. Never quote a number from this mode to a customer.
          </Alert>
          <Toggle checked={forceLocal} onChange={({ detail }) => onForceLocal(detail.checked)} data-testid="cfg-force-local">
            Force local render mode even if a backend is reachable
          </Toggle>
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Verified spec provenance</Header>}>
        <SpaceBetween size="s">
          <Box variant="p">
            Field widths: Barcode, Package, Intelligent Mail® Specification <strong>Rev E</strong>,
            USPS2000508, CAGE 27085, 12/29/2011, §8.5 Tables 6–12. MOD 10 worked example: USPS{' '}
            <strong>Publication 199 v35</strong> §4.6.
          </Box>
          <Box variant="p">
            The Channel Application Identifier selects the MID width: <code>92</code> → 9-digit MID,{' '}
            <code>93</code> → 6-digit MID. The <code>420</code>+ZIP routing AI is{' '}
            <strong>encoded in the bars but suppressed from the human-readable text</strong> — a
            30-digit C03 prints as 22 digits. MOD 10 is the <em>data</em> check digit; MOD 103 is the
            separate Code 128 <em>symbology</em> check digit. Both are computed and they are not the
            same thing.
          </Box>
          <KeyValuePairs
            columns={2}
            items={[
              {
                label: 'shared/impb/impb.js SHA-256 (verified at build time)',
                value: (
                  <code style={{ wordBreak: 'break-all' }} data-testid="impb-sha">
                    {IMPB_SHA256}
                  </code>
                ),
              },
              { label: 'Bundle built at', value: <code>{BUILT_AT}</code> },
            ]}
          />
          <Box variant="small" color="text-body-secondary">
            <code>web/vendor/impb.js</code> is a byte-identical copy of{' '}
            <code>shared/impb/impb.js</code> (71/71 conformance checks). <code>web/build.mjs</code>{' '}
            recomputes both digests and <strong>refuses to build</strong> on a mismatch, so the
            browser and the Lambdas cannot disagree about what a valid PIC is. Symbology is{' '}
            <code>web/barcode.js</code> (39/39 checks), which decodes its own rendered bars back to
            digits.
          </Box>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
}
